import React, { useContext } from 'react'
import {
  PlayPauseButton,
  SkipButton,
  Time,
  Slider,
  TrackDetails,
  AudioContext,
} from 'rewact'
import { ModalContext } from 'modaaals'
import cn from 'classnames'
import pickBy from 'lodash.pickby'

import * as Icon from '../Icons'
import SelectionContext from '../../context/SelectionContext'
import TrackContext from '../../context/TrackContext'
import { useHistory, useLocation, Link } from 'react-router-dom'

import styles from './player.module.css'

function MusicPlayer() {
  const { playerTrack, loop, setLoop, tracks, setPlayerTrack, setQuery } =
    useContext(TrackContext)
  let history = useHistory()
  let location = useLocation()

  const { isMuted, toggleMute } = useContext(AudioContext)
  const { selectionState, setSelectionState } = useContext(SelectionContext)
  const { openModal, modal } = useContext(ModalContext)

  function toggleSelection() {
    const { slug, artist, name, image } = playerTrack
    setSelectionState((selection) => {
      if (selection[slug]) {
        return pickBy(selection, (val, key) => key !== slug)
      } else {
        return {
          [slug]: {
            slug,
            artist,
            name,
            image,
          },
          ...selection,
        }
      }
    })
  }

  const inApp = /^\/app/.test(location.pathname)
  const showInModal =
    modal && (modal.type === 'quote' || modal.type === 'payment')
  const hide = /^\/(login|reset)/.test(location.pathname) && !showInModal

  return hide ? null : (
    <div
      className={cn(styles.playerWrapper, {
        [styles.fixed]: showInModal,
      })}
    >
      <div className={styles.innerWrapper}>
        <TrackDetails
          name={
            <span
              tabIndex={0}
              role="button"
              className={styles.playerArtist}
              onClick={() => {
                setQuery(() => ({
                  artist: [playerTrack.artist],
                }))
                if (!inApp) {
                  history.push('/app')
                }
              }}
            >
              {playerTrack.artist}
            </span>
          }
          artist={
            <span className={styles.playerTrackName}>{playerTrack.name}</span>
          }
          img={
            playerTrack.image
              ? playerTrack.image.startsWith('/') ||
                playerTrack.image.startsWith('data:image')
                ? playerTrack.image
                : `${process.env.REACT_APP_COVER_URI}/m/${playerTrack.image}`
              : require('../../images/placeholder.jpg')
          }
          styles={{
            trackDetails: (base) => ({
              ...base,
              display: 'flex',
              alignItems: 'center',
              width: 340,
              '@media (min-width: 670px)': {
                marginRight: 40,
              },
              order: 1,
            }),
            trackDetailsImg: (base) => ({
              ...base,
              width: 72,
              height: 72,
              maxWidth: 'initial',
              borderRadius: 20,
              marginRight: 25,
              display: 'block',
              alignSelf: 'center',
              verticalAlign: null,
            }),
            trackDetailsText: (base) => ({
              ...base,
              fontSize: 21,
            }),
            trackDetailsTextName: (base) => ({
              ...base,
              color: '#646464',
              fontSize: 15,
              marginTop: 3,
            }),
          }}
        />
        <div className={styles.buttons}>
          <button
            className={cn(styles.playerExtrasBtn, styles.onlyMobile, {
              [styles.active]: loop,
            })}
            style={{ marginRight: 'auto' }}
            onClick={() => setLoop((loop) => !loop)}
          >
            <Icon.Loop />
          </button>
          <SkipButton
            onClick={(event, { play }) => {
              const currentIndex = tracks
                .map((t) => t.slug)
                .indexOf(playerTrack.slug)
              let nextIndex
              nextIndex =
                currentIndex === 0
                  ? tracks.length - 1
                  : currentIndex === -1
                  ? 0
                  : currentIndex - 1
              const { id, artist, name, slug, image } = tracks[nextIndex]
              setPlayerTrack({
                id,
                artist,
                name,
                slug,
                image,
              })
              setTimeout(() => play(), 100)
            }}
            styles={{
              skipButton: (base) => ({
                ...base,
                ...skipButtonStyles,
                '@media (min-width: 670px)': {
                  marginLeft: 40,
                  marginRight: 8,
                },
                transform: 'translate3d(0px, 0px, 0px)',
              }),
            }}
          />
          <PlayPauseButton
            styles={{
              playPauseButton: (base) => ({
                ...base,
                width: 68,
                height: 68,
                padding: 8,
                '--rw-accent': '#282828',
                borderRadius: '100%',
                boxShadow: '0 5px 25px 0 rgba(17,1,2,.13)',
                '@media (min-width: 670px)': {
                  boxShadow: 'none',
                  padding: 0,
                  width: 54,
                  height: 54,
                },
                transform: 'translate3d(0px, 0px, 0px)',
              }),
              progressLine: (base) => ({
                ...base,
                stroke: '#b8b8b8',
              }),
            }}
          />
          <SkipButton
            direction="next"
            onClick={(event, { play }) => {
              if (tracks.length > 0) {
                const currentIndex = tracks
                  .map((t) => t.slug)
                  .indexOf(playerTrack.slug)
                let nextIndex
                if (tracks.length - 1 > currentIndex) {
                  nextIndex = currentIndex + 1
                } else {
                  nextIndex = 0
                }
                const { id, artist, name, slug, image } = tracks[nextIndex]
                setPlayerTrack({
                  id,
                  artist,
                  name,
                  slug,
                  image,
                })
                setTimeout(() => play(), 100)
              }
            }}
            styles={{
              skipButton: (base) => ({
                ...base,
                ...skipButtonStyles,
                '@media (min-width: 670px)': {
                  marginRight: 40,
                  marginLeft: 8,
                },
                transform: 'translate3d(0px, 0px, 0px)',
              }),
            }}
          />
          <button
            className={cn(styles.playerExtrasBtn, styles.onlyMobile, {
              [styles.active]: isMuted,
            })}
            onClick={toggleMute}
          >
            {isMuted ? <Icon.Mute /> : <Icon.Volume />}
          </button>
        </div>
        <div className={styles.slider}>
          <Time
            styles={{
              time: () => ({
                ...timeStyles,
                marginRight: 12,
                left: 0,
              }),
            }}
          />
          <Slider
            styles={{
              slider: (base) => ({
                ...base,
                flex: 1,
                '--rw-accent': 'var(--c-red)',
              }),
            }}
          />
          <Time
            styles={{
              time: () => ({
                ...timeStyles,
                marginLeft: 12,
                right: 0,
              }),
            }}
            remaining
          />
        </div>

        <div className={styles.playerExtras}>
          <button
            className={cn(styles.playerExtrasBtn, styles.onlyDesktop, {
              [styles.active]: isMuted,
            })}
            onClick={toggleMute}
          >
            {isMuted ? <Icon.Mute /> : <Icon.Volume />}
          </button>
          <button
            className={cn(styles.playerExtrasBtn, styles.onlyDesktop, {
              [styles.active]: loop,
            })}
            onClick={() => setLoop((loop) => !loop)}
          >
            <Icon.Loop className={styles.loopIcon} />
          </button>
          <button
            style={{ marginRight: 0 }}
            onClick={toggleSelection}
            className={cn(styles.playerExtrasBtn, {
              [styles.active]: selectionState[playerTrack.slug],
            })}
          >
            <Icon.Heart className={styles.heartIcon} />
          </button>

          <button
            className={styles.darkButton}
            onClick={() => {
              openModal({
                type: 'quote',
                selectionState: [playerTrack],
              })
            }}
          >
            <Icon.Quote className={styles.darkButtonIcon} />
            <span>Quote</span>
          </button>

          <Link
            to="app"
            style={{ color: '#141520' }}
            className={cn(styles.playerExtrasBtn, styles.onlyMobile, {
              [styles.active]: selectionState[playerTrack.slug],
            })}
            onClick={() => window.scrollTo(0, 0)}
          >
            <Icon.TracksList />
          </Link>
        </div>
      </div>
    </div>
  )
}

export default MusicPlayer

const timeStyles = {
  color: '#67676b',
  fontWeight: 800,
  fontSize: 11,
  marginTop: 2,
  position: 'absolute',
  '@media (min-width: 670px)': {
    position: 'relative',
    fontSize: 14,
    top: 'auto',
    left: 'auto',
    right: 'auto',
  },
  top: '1.5em',
}

const skipButtonStyles = {
  fontSize: '0.65em',
  color: '#b8b8b8',
  '&:hover': {
    color: '#282828',
  },
}
